import React, { useEffect, useRef } from 'react';
import { Calendar, Users, MapPin, Star } from 'lucide-react';

interface StatItem {
  icon: React.ReactNode;
  value: string;
  label: string;
  delay: number;
}

const StatCard: React.FC<StatItem> = ({ icon, value, label, delay }) => {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('opacity-100', 'translate-x-0');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });

    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={cardRef}
      className={`opacity-0 ${delay % 2 === 0 ? '-translate-x-8' : 'translate-x-8'} transition-all duration-700 ease-out`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="bg-white rounded-lg shadow-lg p-12 h-64 flex items-center justify-center">
        <div className="flex flex-col items-center text-center">
          <div className="text-[#CC0000] mb-6 bg-[#FFEBEB] p-4 rounded-full">{icon}</div>
          <div className="text-4xl font-bold text-[#1A1A1A] mb-3">{value}</div>
          <div className="text-gray-600 text-lg">{label}</div>
        </div>
      </div>
    </div>
  );
};

const Awards: React.FC = () => {
  const stats: StatItem[] = [
    {
      icon: <Calendar size={32} />,
      value: "4+",
      label: "Years of Innovation",
      delay: 0
    },
    {
      icon: <Users size={32} />,
      value: "100+",
      label: "Skilled Team Members",
      delay: 100
    },
    {
      icon: <MapPin size={32} />,
      value: "15+",
      label: "States We Serve",
      delay: 200
    },
    {
      icon: <Star size={32} />,
      value: "5K+",
      label: "Satisfied Customers",
      delay: 300
    }
  ];

  return (
    <section className="py-20 bg-[#F5F5F5]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-[#1A1A1A] mb-4">Our Impact</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Since our inception, Dawki Mobility has been driving the electric mobility revolution in India,
            delivering innovative solutions and building lasting relationships with customers across the nation.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 items-stretch">
          {stats.map((stat, index) => (
            <StatCard key={index} {...stat} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Awards;